/**
 * @boli/core - useBidding Hook
 *
 * Platform-agnostic React hook for bidding functionality.
 * Works on BOTH Web (Phase 1) and React Native (Phase 2) with ZERO changes.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Auction, Bid, BidResponse, RealtimeEvent, BidPlacedEvent } from '@boli/types';
import { getBiddingService, BiddingService } from '../services/bidding.service';

// ============================================================================
// HOOK: useAuction
// ============================================================================

export interface UseAuctionOptions {
  pollInterval?: number; // Fallback polling interval (only used if realtime unavailable)
  autoSubscribe?: boolean;
}

export interface UseAuctionResult {
  auction: Auction | null;
  isLoading: boolean;
  error: Error | null;
  isRealtimeConnected: boolean;
  refetch: () => Promise<void>;
}

export function useAuction(
  auctionId: string,
  options: UseAuctionOptions = {}
): UseAuctionResult {
  const { pollInterval = 5000, autoSubscribe = true } = options;

  const [auction, setAuction] = useState<Auction | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);

  const biddingService = useRef<BiddingService | null>(null);

  const fetchAuction = useCallback(async () => {
    try {
      if (!biddingService.current) {
        biddingService.current = getBiddingService();
      }

      const data = await biddingService.current.getAuction(auctionId);
      setAuction(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch auction'));
    } finally {
      setIsLoading(false);
    }
  }, [auctionId]);

  useEffect(() => {
    // Initial fetch
    fetchAuction();

    let unsubscribe: (() => void) | undefined;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    // Prioritize realtime subscription over polling
    if (autoSubscribe && biddingService.current) {
      try {
        unsubscribe = biddingService.current.subscribeToBids(
          auctionId,
          (event: RealtimeEvent<BidPlacedEvent>) => {
            // Realtime is working, mark as connected
            setIsRealtimeConnected(true);

            if (event.payload?.auction) {
              setAuction((prev: Auction | null) =>
                prev
                  ? {
                      ...prev,
                      currentHighBid: event.payload.auction.currentHighBid,
                      currentHighBidderId: event.payload.auction.currentHighBidderId,
                      endTimestamp: event.payload.auction.endTimestamp,
                      extensionCount: event.payload.auction.extensionCount,
                    }
                  : prev
              );
            }
          }
        );
        setIsRealtimeConnected(true);
      } catch {
        // Realtime subscription failed, fall back to polling
        setIsRealtimeConnected(false);
      }
    }

    // Only use polling as fallback when realtime is unavailable
    // Check after a short delay to see if realtime connected
    const pollFallbackTimeout = setTimeout(() => {
      if (!isRealtimeConnected) {
        intervalId = setInterval(fetchAuction, pollInterval);
      }
    }, 1000);

    return () => {
      clearTimeout(pollFallbackTimeout);
      if (intervalId) clearInterval(intervalId);
      unsubscribe?.();
    };
  }, [auctionId, pollInterval, autoSubscribe, fetchAuction, isRealtimeConnected]);

  return {
    auction,
    isLoading,
    error,
    isRealtimeConnected,
    refetch: fetchAuction,
  };
}

// ============================================================================
// HOOK: useBids
// ============================================================================

export interface UseBidsResult {
  bids: Bid[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useBids(auctionId: string, limit: number = 50): UseBidsResult {
  const [bids, setBids] = useState<Bid[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const biddingService = useRef<BiddingService | null>(null);

  const fetchBids = useCallback(async () => {
    try {
      if (!biddingService.current) {
        biddingService.current = getBiddingService();
      }

      const data = await biddingService.current.getBids(auctionId, limit);
      setBids(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch bids'));
    } finally {
      setIsLoading(false);
    }
  }, [auctionId, limit]);

  useEffect(() => {
    fetchBids();

    // Subscribe to new bids
    let unsubscribe: (() => void) | undefined;

    if (biddingService.current) {
      unsubscribe = biddingService.current.subscribeToBids(
        auctionId,
        (event: RealtimeEvent<BidPlacedEvent>) => {
          if (event.payload?.bid) {
            setBids((prev: Bid[]) => [event.payload.bid, ...prev].slice(0, limit));
          }
        }
      );
    }

    return () => {
      unsubscribe?.();
    };
  }, [auctionId, limit, fetchBids]);

  return {
    bids,
    isLoading,
    error,
    refetch: fetchBids,
  };
}

// ============================================================================
// HOOK: usePlaceBid
// ============================================================================

export interface UsePlaceBidResult {
  placeBid: (amount: number, userId: string) => Promise<BidResponse>;
  isPlacing: boolean;
  lastBid: BidResponse | null;
  error: Error | null;
  reset: () => void;
}

export function usePlaceBid(auctionId: string): UsePlaceBidResult {
  const [isPlacing, setIsPlacing] = useState(false);
  const [lastBid, setLastBid] = useState<BidResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const biddingService = useRef<BiddingService | null>(null);

  const placeBid = useCallback(
    async (amount: number, userId: string): Promise<BidResponse> => {
      setIsPlacing(true);
      setError(null);

      try {
        if (!biddingService.current) {
          biddingService.current = getBiddingService();
        }

        const response = await biddingService.current.placeBid(auctionId, amount, userId);
        setLastBid(response);

        if (!response.success && response.error) {
          setError(new Error(response.error.message));
        }

        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to place bid');
        setError(error);
        return {
          success: false,
          error: {
            code: 'UNKNOWN_ERROR',
            message: error.message,
          },
        };
      } finally {
        setIsPlacing(false);
      }
    },
    [auctionId]
  );

  const reset = useCallback(() => {
    setLastBid(null);
    setError(null);
  }, []);

  return {
    placeBid,
    isPlacing,
    lastBid,
    error,
    reset,
  };
}

// ============================================================================
// HOOK: useCountdown
// ============================================================================

export interface UseCountdownResult {
  timeRemaining: number;
  formatted: string;
  isEnded: boolean;
  isEndingSoon: boolean;
  hours: number;
  minutes: number;
  seconds: number;
}

export function useCountdown(
  endTimestamp: number,
  endingSoonThreshold: number = 10000
): UseCountdownResult {
  const [timeRemaining, setTimeRemaining] = useState(() => {
    try {
      const service = getBiddingService();
      return service.getTimeRemaining(endTimestamp);
    } catch {
      return Math.max(0, endTimestamp - Date.now());
    }
  });

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const service = getBiddingService();
        const remaining = service.getTimeRemaining(endTimestamp);
        setTimeRemaining(remaining);

        if (remaining <= 0) {
          clearInterval(interval);
        }
      } catch {
        const remaining = Math.max(0, endTimestamp - Date.now());
        setTimeRemaining(remaining);
      }
    }, 100); // Update every 100ms for smooth countdown

    return () => clearInterval(interval);
  }, [endTimestamp]);

  const hours = Math.floor(timeRemaining / 3600000);
  const minutes = Math.floor((timeRemaining % 3600000) / 60000);
  const seconds = Math.floor((timeRemaining % 60000) / 1000);

  let formatted: string;
  if (hours > 0) {
    formatted = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  } else {
    formatted = `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }

  return {
    timeRemaining,
    formatted,
    isEnded: timeRemaining <= 0,
    isEndingSoon: timeRemaining > 0 && timeRemaining <= endingSoonThreshold,
    hours,
    minutes,
    seconds,
  };
}

// ============================================================================
// HOOK: useBidEligibility
// ============================================================================

export interface UseBidEligibilityResult {
  canBid: boolean;
  reason?: string;
  cooldownMs?: number;
  kycRequired?: boolean;
  paymentRequired?: boolean;
  checkEligibility: (amount: number) => Promise<void>;
}

export function useBidEligibility(
  userId: string,
  auctionId: string
): UseBidEligibilityResult {
  const [canBid, setCanBid] = useState(true);
  const [reason, setReason] = useState<string | undefined>();
  const [cooldownMs, setCooldownMs] = useState<number | undefined>();
  const [kycRequired, setKycRequired] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(false);

  const biddingService = useRef<BiddingService | null>(null);

  const checkEligibility = useCallback(
    async (amount: number) => {
      try {
        if (!biddingService.current) {
          biddingService.current = getBiddingService();
        }

        const eligibility = await biddingService.current.canUserBid(
          userId,
          auctionId,
          amount
        );

        setCanBid(eligibility.canBid);
        setReason(eligibility.reason);
        setCooldownMs(eligibility.cooldownMs);
        setKycRequired(eligibility.kycRequired ?? false);
        setPaymentRequired(eligibility.paymentRequired ?? false);
      } catch (err) {
        setCanBid(false);
        setReason('Unable to verify eligibility');
      }
    },
    [userId, auctionId]
  );

  return {
    canBid,
    reason,
    cooldownMs,
    kycRequired,
    paymentRequired,
    checkEligibility,
  };
}

// ============================================================================
// HOOK: useSuggestedBids
// ============================================================================

export interface UseSuggestedBidsResult {
  minimumBid: number;
  increments: number[];
  suggestedAmounts: number[];
}

export function useSuggestedBids(currentHighBid: number): UseSuggestedBidsResult {
  const [minimumBid, setMinimumBid] = useState(0);
  const [increments, setIncrements] = useState<number[]>([]);
  const [suggestedAmounts, setSuggestedAmounts] = useState<number[]>([]);

  useEffect(() => {
    try {
      const service = getBiddingService();

      const min = service.getMinimumBid(currentHighBid);
      setMinimumBid(min);

      const incrs = service.getSuggestedIncrements(currentHighBid);
      setIncrements(incrs);

      setSuggestedAmounts(incrs.map((i) => currentHighBid + i));
    } catch {
      // Fallback calculations
      const base = Math.max(100, Math.floor(currentHighBid * 0.05));
      setMinimumBid(currentHighBid + base);
      setIncrements([base, base * 2, base * 5, base * 10]);
      setSuggestedAmounts([
        currentHighBid + base,
        currentHighBid + base * 2,
        currentHighBid + base * 5,
        currentHighBid + base * 10,
      ]);
    }
  }, [currentHighBid]);

  return {
    minimumBid,
    increments,
    suggestedAmounts,
  };
}
